function _EMOTION_STRINGIFIED_CSS_ERROR__() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; }
import { css } from '@emotion/react';
import { SelectProvider, useOption, useSelect } from '@mui/base';
import { useMemo, useState } from 'react';
import IconX from '~icons/bx/x';
import IconChevronDown from '~icons/ci/chevron-down';
import { AnimatedPopover, lineClampStyle, notchedPopperModifier, sameWidthPopperModifier } from '@arc/frontend/ui-common';
import { LoadingIndicator } from '@arc/frontend/ui-graphics';
import { theme, typography } from '@arc/frontend/ui-theme';
import { selectorStyles } from './styles';
const styles = {
  helperText: /*#__PURE__*/css(typography.inputHelper, "font-size:12px;" + (process.env.NODE_ENV === "production" ? "" : ";label:helperText;")),
  helperTextError: /*#__PURE__*/css({
    color: theme.colors.palettes.error.dark
  }, process.env.NODE_ENV === "production" ? "" : ";label:helperTextError;"),
  loading: process.env.NODE_ENV === "production" ? {
    name: "1kfuls6",
    styles: "position:relative;width:2rem;height:2rem;right:-0.25rem"
  } : {
    name: "1mdcxh1-loading",
    styles: "position:relative;width:2rem;height:2rem;right:-0.25rem;label:loading;",
    toString: _EMOTION_STRINGIFIED_CSS_ERROR__
  }
};
function uniqPush(array, item) {
  return array.includes(item) ? array : [...array, item];
}
export function SelectorSelect({
  dropdownMaxHeight = '40vh',
  disabled,
  archived,
  error,
  readOnly,
  placeholder,
  items,
  value,
  onChange,
  popoverRef,
  testid,
  helperText,
  inputRef,
  removeButton,
  Icon: IconProp = IconChevronDown,
  isLoading,
  renderOption
}) {
  const [buttonElement, setButtonElement] = useState(null);
  const Icon = useMemo(() => value && removeButton ? IconX : IconProp, [IconProp, removeButton, value]);
  const {
    getButtonProps,
    open,
    getListboxProps,
    contextValue
  } = useSelect({
    buttonRef: setButtonElement,
    // `useSelect` expects `value` to be in `options`, so push it if it's not.
    options: uniqPush(items, value).map(item => ({
      label: item,
      value: item
    })),
    value,
    onChange: (_event, value_) => {
      if (value_ === null) {
        // We don't support clearing selected values so this should never happen.
        console.warn('SelectorSelect: value is null');
        return;
      }
      onChange(value_);
    },
    disabled
  });
  return <>
			{readOnly ? <div css={selectorStyles.container({
      error,
      disabled,
      archived,
      readOnly
    })}>
					<div css={[selectorStyles.input({
        disabled,
        archived,
        readOnly
      }), "cursor:default;resize:none;word-break:break-word;" + (process.env.NODE_ENV === "production" ? "" : ";label:SelectorSelect;")]}>
						{value || <span>&nbsp;</span>}
					</div>
				</div> : null}
			<button {...getButtonProps()} type="button" css={[selectorStyles.container({
      error,
      disabled,
      archived,
      readOnly
    }), {
      textAlign: 'left',
      cursor: disabled ? 'default' : 'pointer',
      display: readOnly ? 'none' : undefined
    }, process.env.NODE_ENV === "production" ? "" : ";label:SelectorSelect;"]} data-testid={`${testid}-button`}>
				<div ref={inputRef} css={[lineClampStyle(), selectorStyles.input({
        disabled,
        archived,
        readOnly
      }), "resize:none;", value ? undefined : /*#__PURE__*/css(typography.placeholder, "opacity:1;" + (process.env.NODE_ENV === "production" ? "" : ";label:SelectorSelect;")), process.env.NODE_ENV === "production" ? "" : ";label:SelectorSelect;"]} data-testid={`${testid}-input`}>
					{value || placeholder}
				</div>
				{isLoading ? <div css={styles.loading}>
						<LoadingIndicator />
					</div> : null}
				{readOnly ? null : <div>
						<Icon onClick={value && removeButton ? () => onChange('') : undefined} css={selectorStyles.adornmentIcon} width={20} height={20} />
					</div>}
			</button>
			{disabled || readOnly ? null : <AnimatedPopover ref={popoverRef} open={open} anchorEl={buttonElement} PopperProps={{
      placement: 'bottom-start',
      keepMounted: true,
      modifiers: [{
        name: 'offset',
        options: {
          offset: [0, -2]
        }
      }, notchedPopperModifier({
        notchDepth: 1
      }), sameWidthPopperModifier]
    }} data-testid={`${testid}-popover`}>
					<SelectProvider value={contextValue}>
						<ul {...getListboxProps()} css={[selectorStyles.popover(dropdownMaxHeight), selectorStyles.list, process.env.NODE_ENV === "production" ? "" : ";label:SelectorSelect;"]}>
							{items.map((item, index) => <Option key={`${item}${index}`} value={item} renderOption={renderOption} />)}
						</ul>
					</SelectProvider>
				</AnimatedPopover>}
			{helperText ? <div css={[styles.helperText, error && styles.helperTextError, process.env.NODE_ENV === "production" ? "" : ";label:SelectorSelect;"]}>
					{helperText}
				</div> : null}
		</>;
}
function Option({
  value,
  disabled = false,
  renderOption
}) {
  const {
    getRootProps,
    highlighted
  } = useOption({
    value,
    disabled,
    label: value
  });
  const rendered = renderOption?.(value, getRootProps, highlighted);
  if (rendered) {
    return rendered;
  }
  return <li {...getRootProps()} data-highlighted={highlighted || undefined}
  // `data-value` is only used by tests.
  data-value={value} css={selectorStyles.listItem}>
			{value}
		</li>;
}